// Physical Verification (Cycle Count / Stock Reconciliation) — sheet-style
// count entry per lot with live variance calculation. Optionally auto-posts
// Adjust txns to reconcile the ledger.
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function PhysicalVerification() {
  const [godowns, setGodowns] = useState([]);
  const [godownId, setGodownId] = useState("");
  const [lots, setLots] = useState([]);
  const [counts, setCounts] = useState({}); // lot_id -> counted_qty
  const [remarks, setRemarks] = useState("");
  const [postAdj, setPostAdj] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/godowns/").then(r => setGodowns(r.data)); }, []);
  const loadLots = async () => {
    const url = godownId ? `/stock/on-hand?godown_id=${godownId}&include_zero=true` : `/stock/on-hand?include_zero=true`;
    const { data } = await api.get(url);
    setLots(data?.rows || []);
    setCounts({});
  };

  const variances = useMemo(() => {
    return lots.map(l => {
      const cnt = counts[l.lot_id];
      const v = cnt === undefined || cnt === "" ? null : parseFloat(cnt) - l.available_qty;
      return { ...l, counted: cnt, variance: v };
    });
  }, [lots, counts]);
  const totalVar = variances.reduce((s, r) => s + (r.variance || 0), 0);

  const submit = async () => {
    const lines = Object.entries(counts).filter(([, v]) => v !== "" && v !== undefined).map(([lot_id, v]) => ({ lot_id, counted_qty: parseFloat(v) }));
    if (lines.length === 0) { toast.error("Enter at least one count"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/stock/physical-verification", {
        godown_id: godownId, lines, post_adjustments: postAdj, remarks,
      });
      toast.success(`PV posted · variance ${data.total_variance}${postAdj ? " · ledger adjusted" : ""}`);
      setResult(data);
      if (postAdj) await loadLots();
    } catch (e) { toast.error(e?.response?.data?.detail || "PV failed"); }
    setBusy(false);
  };

  const inp = "w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]";
  return (
    <div className="space-y-4" data-testid="pv-page">
      <div className="overline text-[10px] text-[var(--accent)]">INVENTORY</div>
      <h1 className="text-2xl font-semibold">Physical Verification · Cycle Count</h1>
      <p className="text-sm text-zinc-400">Enter counted qty per lot. Variances highlight in red/green. Toggle auto-adjust to reconcile the ledger in one go.</p>
      <div className="tactical-card p-4 flex flex-wrap items-end gap-3">
        <label className="text-xs uppercase tracking-widest text-zinc-500 space-y-1">
          <div>Godown (optional filter)</div>
          <select data-testid="pv-godown" value={godownId} onChange={e => setGodownId(e.target.value)} className={inp + " min-w-[220px]"}>
            <option value="">— All Godowns —</option>
            {godowns.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </label>
        <button data-testid="pv-load" onClick={loadLots} className="h-9 px-4 border border-[var(--border)] rounded-sm text-sm hover:border-[var(--accent)]">Load Lots</button>
        <label className="flex items-center gap-2 text-xs text-zinc-400 ml-auto">
          <input data-testid="pv-post-adj" type="checkbox" checked={postAdj} onChange={e => setPostAdj(e.target.checked)} />
          Auto-post Adjust txns on save
        </label>
      </div>
      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="text-left px-5 py-3">Lot No</th>
              <th className="text-left px-5 py-3">RM</th>
              <th className="text-right px-5 py-3">System Qty</th>
              <th className="text-right px-5 py-3">Counted Qty</th>
              <th className="text-right px-5 py-3">Variance</th>
            </tr>
          </thead>
          <tbody>
            {variances.length === 0 && <tr><td colSpan="5" className="text-center px-5 py-6 text-zinc-500">Load lots to start counting</td></tr>}
            {variances.map(l => (
              <tr key={l.lot_id} className="border-b border-[var(--border)]" data-testid={`pv-row-${l.lot_id?.slice(0,8)}`}>
                <td className="px-5 py-2 font-mono text-[11px]">{l.lot_no}</td>
                <td className="px-5 py-2 font-mono text-[10px] text-zinc-500">{l.raw_material_id?.slice(0,8)}</td>
                <td className="px-5 py-2 text-right font-mono">{l.available_qty}</td>
                <td className="px-5 py-2 text-right">
                  <input data-testid={`pv-cnt-${l.lot_id?.slice(0,8)}`} type="number" step="0.001" value={l.counted ?? ""} onChange={e => setCounts({ ...counts, [l.lot_id]: e.target.value })}
                    className="w-24 h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm text-right focus:outline-none focus:border-[var(--accent)]" />
                </td>
                <td className={`px-5 py-2 text-right font-bold ${l.variance === null ? "text-zinc-500" : l.variance === 0 ? "text-zinc-400" : l.variance > 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {l.variance === null ? "—" : (l.variance > 0 ? "+" : "") + l.variance.toFixed(3)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {variances.length > 0 && (
        <div className="tactical-card p-4 flex flex-wrap items-center gap-3">
          <div className="text-xs uppercase tracking-widest text-zinc-500">Total Variance</div>
          <div className={`text-2xl font-bold font-mono ${totalVar === 0 ? "text-zinc-300" : totalVar > 0 ? "text-emerald-400" : "text-red-400"}`}>
            {(totalVar > 0 ? "+" : "") + totalVar.toFixed(3)}
          </div>
          <input value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Remarks / audit notes" className={inp + " flex-1 min-w-[240px]"} />
          <button data-testid="pv-submit" onClick={submit} disabled={busy}
            className="h-10 px-6 bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold rounded-sm disabled:opacity-50">
            {busy ? "Posting…" : "Save PV Session"}
          </button>
        </div>
      )}
      {result && (
        <div className="tactical-card p-4">
          <div className="text-sm font-semibold mb-2">Last PV Session · <span className="font-mono text-xs">{result.session_id?.slice(0, 12)}…</span></div>
          <div className="text-xs text-zinc-400">
            Adjusted {result.lines?.filter(l => l.adjusted).length || 0} of {result.lines?.length || 0} lines · total variance {result.total_variance}
          </div>
        </div>
      )}
    </div>
  );
}
